import { CommonModule, CurrencyPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit, Input, OnChanges, SimpleChanges, Output, EventEmitter, signal, ViewChild, WritableSignal, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router, Params, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { DocumentsService } from '@k12/documents';
import { AppInsightsService } from '@k12/error_handler';
import { LoadingSpinnerService } from '@k12/loading';
import { MessageCenterSendDialogComponent } from '@k12/messaging';
import {
  AccountMessageTopic,
  Comment,
  DocumentsList,
  Email,
  EmployeeSubService,
  EmployeeSummary,
  HouseholdRecordVm,
  MessageCenterPreviewData,
  ProviderData,
  SchoolRecordVm,
  Task,
  FieldOption,
  SeverityLevel,
  CommentType,
  SvgIcon,
  ProviderInfoStatus,
  ToastMessageType,
  AdminUserType, PartnerTypeIdToUserType, UserRecordType,
  IssueHistory, IssueThreadParentMatch,
  HouseholdStudent, Address,
  HouseHoldDetails,
  CommunicationTableMetadata,
  Paged,
  CommunicationTableItem,
  EmployeeSummaryAdminPayload,
  IncomeVerificationDetails,
  AcademicYear
} from '@k12/models';
import { BreadcrumbService } from '@k12/navigation';
import { ToastMessageService } from '@k12/notifications';
import { ColumnDefinition } from '@k12/tables';
import { TasksService } from '@k12/tasks';
import { formatSchoolAccountLabel } from '@k12/app-services';
import {
  IIssueTrackingService,
  ISSUE_TRACKING_SERVICE,
  IUserRecordCommentsService,
  IUserRecordCommunicationsService,
  IUserRecordEmployeeService,
  IUserRecordMessagesService,
  IUserRecordProviderDetailsService,
  IUserRecordSystemService,
  USER_RECORD_COMMENTS_SERVICE,
  USER_RECORD_COMMUNICATIONS_SERVICE,
  USER_RECORD_EMPLOYEE_SERVICE,
  USER_RECORD_MESSAGES_SERVICE,
  USER_RECORD_PROVIDER_DETAILS_SERVICE,
  USER_RECORD_SYSTEM_SERVICE
} from '@k12/tokens';
import { UserInfoPanelComponent } from '@k12/user-info';
import { catchError, firstValueFrom, of, Subject, switchMap, takeUntil } from 'rxjs';
import {
  ContactsColumnDefinitions,
  DocumentColumnDefinitions,
  EmailColumnDefinitions,
  EmployeesColumnDefinitions,
  IssueTrackingColumnDefinitions,
  MessagesColumnDefinitions,
  StudentsColumnDefinitions,
  TaskColumnDefinitions
} from './user-record-data';
import { UserRecordTableComponent } from './user-record-table/user-record-table.component';
import { HouseholdService } from '@k12/households';
import { MatSelectModule } from '@angular/material/select';
import { AwardsService } from '@k12/awards';
import { FormatDatePipe } from '@k12/pipes';

type UserRecordSortableTableRow =
  | Task
  | Email
  | DocumentsList
  | EmployeeSummary
  | MessageCenterPreviewData
  | IssueHistory;

type HouseholdStudentRecord = Pick<HouseholdStudent, 'id' | 'firstName' | 'lastName'> &
  Partial<Omit<HouseholdStudent, 'id' | 'firstName' | 'lastName'>>;

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-user-record',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatSelectModule,
    RouterModule,
    UserInfoPanelComponent,
    UserRecordTableComponent
  ],
  providers: [CurrencyPipe],
  styleUrls: ['./user-record.component.scss'],
  templateUrl: './user-record.component.html'
})
export class UserRecordComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild(MatSort) sortTables: MatSort;

  @Input() environment;
  @Input() parentUrl: string;
  @Input() scrollable: boolean = false;
  @Input() userDetails: SchoolRecordVm | HouseholdRecordVm | null = null;
  @Input() userType: AdminUserType = AdminUserType.Provider;
  @Input() selectedAcademicYearId: number | null = null;

  @Output() editIssueClick = new EventEmitter<IssueHistory>();
  @Output() newIssue = new EventEmitter<string>();
  @Output() openCommentsDrawer = new EventEmitter<Comment[]>();

  accountName: string = '';
  apiUrl: string = '';
  commentType = CommentType;
  dataSource: MatTableDataSource<UserRecordSortableTableRow>;
  employeeData: EmployeeSummary[] = [];
  loading: boolean = true;
  private shouldHideGlobalSpinnerOnInitialLoad = false;
  preserveBreadcrumbs: boolean = false;
  rowData: unknown;
  schoolStatus = signal<ProviderInfoStatus>(ProviderInfoStatus.OnHold);
  schoolRestrictionMessage = signal<string | null>(null);
  userData: WritableSignal<ProviderData | SchoolRecordVm | HouseholdRecordVm | null> = signal(null);
  userId: string;
  userLoading: boolean = true;
  userNameFromState: string = ''; // Store user name from navigation state
  UserRecordType = UserRecordType;
  userTypeId: number;

  // Task Data
  taskData: WritableSignal<Task[]> = signal([]);
  taskColumnDefinitions: ColumnDefinition[] = TaskColumnDefinitions;

  // Email Data
  emailData: WritableSignal<Email[]> = signal([]);
  emailColumnDefinitions: ColumnDefinition[] = EmailColumnDefinitions;

  // Document Data
  documentData: WritableSignal<DocumentsList[]> = signal([]);
  documentColumnDefinitions: ColumnDefinition[] = DocumentColumnDefinitions;

  // Messages Data
  messagesData: WritableSignal<MessageCenterPreviewData[]> = signal([]);
  messagesColumnDefinitions: ColumnDefinition[] = MessagesColumnDefinitions;

  // Employees Data
  employeesData: WritableSignal<EmployeeSummary[]> = signal([]);
  employeesColumnDefinitions: ColumnDefinition[] = EmployeesColumnDefinitions;

  // Contacts Data
  contactsColumnDefinitions: ColumnDefinition[] = ContactsColumnDefinitions;

  // Issue Tracking Data
  issueTrackingData: WritableSignal<IssueHistory[]> = signal([]);
  issueTrackingColumnDefinitions: ColumnDefinition[] = IssueTrackingColumnDefinitions;

  // Students Data
  studentsData: WritableSignal<HouseholdStudentRecord[]> = signal([]);
  studentsLoading = false;
  studentsColumnDefinitions: ColumnDefinition[] = StudentsColumnDefinitions;

  // Comments Data
  commentsData: WritableSignal<Comment[]> = signal([]);
  internalCommentsData: WritableSignal<Comment[]> = signal([]);
  internalCommentsFilteredData: WritableSignal<Comment[]> = signal([]);

  // Income Verification Data
  incomeVerificationColumnDefinitions = [
    {
      name: 'label',
      header: 'Income Verification Reason',
      width: 70
    },
    {
      name: 'value',
      header: 'Details',
    }
  ];
  incomeVerificationData: WritableSignal<FieldOption[]> = signal([]);
  academicYears: AcademicYear[] = [];
  selectedAcademicYear: AcademicYear | null;
  academicYearId: number | null;
  isLoadingAcademicYears = false;

  readonly adminUserTypes = AdminUserType;
  readonly handMoneyIcon = SvgIcon.HandMoney;
  readonly hourglassClockIcon = SvgIcon.HourglassClock;
  readonly sendMessageDialog = inject(MatDialog);

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly awardsService = inject(AwardsService);
  private readonly appInsightsService = inject(AppInsightsService);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly destroy$ = new Subject<void>();
  private readonly documentsService = inject(DocumentsService);
  private readonly householdService = inject(HouseholdService);
  private readonly route = inject(ActivatedRoute);
  private readonly loadingSpinnerService = inject(LoadingSpinnerService);
  private readonly router = inject(Router);
  private readonly tasksService = inject(TasksService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly issueTrackingService = inject<IIssueTrackingService>(ISSUE_TRACKING_SERVICE, {
    optional: true
  });
  private readonly commentsService = inject<IUserRecordCommentsService>(USER_RECORD_COMMENTS_SERVICE, {
    optional: true
  });
  private readonly communicationsService = inject<IUserRecordCommunicationsService>(
    USER_RECORD_COMMUNICATIONS_SERVICE,
    { optional: true }
  );
  private readonly employeeService = inject<IUserRecordEmployeeService>(USER_RECORD_EMPLOYEE_SERVICE, {
    optional: true
  });
  private readonly messagesService = inject<IUserRecordMessagesService>(USER_RECORD_MESSAGES_SERVICE, {
    optional: true
  });
  private readonly systemService = inject<IUserRecordSystemService>(USER_RECORD_SYSTEM_SERVICE, {
    optional: true
  });
  private readonly userRecordService = inject<IUserRecordProviderDetailsService>(
    USER_RECORD_PROVIDER_DETAILS_SERVICE,
    { optional: true }
  );

  private sendMessageDialogRef: MatDialogRef<MessageCenterSendDialogComponent>;
  private readonly householdYearChange$ = new Subject<number | null>();
  private lastHouseholdRequestKey: string | null = null;

  constructor() {
    this.householdYearChange$
      .pipe(
        switchMap((academicYearId) =>
          this.householdService.getHouseholdDetails(academicYearId, this.userId).pipe(
            catchError((error: unknown) => {
              this.logAndToastError(error);
              this.userData.set(null);
              this.studentsData.set([]);
              this.userLoading = false;
              this.studentsLoading = false;
              return of(null);
            })
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((details) => {
        if (details) {
          this.applyHouseholdYearDetails(details);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.handleDetailsChange(changes, 'userDetails', AdminUserType.Schools);
    this.handleDetailsChange(changes, 'userDetails', AdminUserType.Households);

    // userType can arrive after userDetails; refresh school/household breadcrumbs when type is known
    if (
      changes['userType'] &&
      this.userDetails &&
      !changes['userDetails'] &&
      (this.userType === AdminUserType.Schools || this.userType === AdminUserType.Households)
    ) {
      this.userData.set(this.userDetails);
      this.updateBreadcrumbWithUserName();
    }
  }

  ngOnInit(): void {
    this.initializeComponent();
    this.loadNavigationState();
    this.subscribeToRouteParams();
    this.loadAcademicYears();
  }

  onAcademicYearChange(academicYearId: number): void {
    const academicYear = this.academicYears.find((year) => year.id === academicYearId) ?? null;
    this.applyAcademicYear(academicYear);
    if (this.userType === AdminUserType.Households && this.userId) {
      this.loadHouseholdForAcademicYear(true);
    }
  }

  private loadAcademicYears(): void {
    this.isLoadingAcademicYears = true;
    this.cdr.markForCheck();
    this.awardsService
      .getAcademicYears()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (years) => {
          this.academicYears = years ?? [];
          const current = this.academicYears.find((year) => FormatDatePipe.isTodayWithin(year.startDate, year.endDate));
          this.applyAcademicYear(current ?? this.academicYears[0] ?? null);
          this.isLoadingAcademicYears = false;
          if (this.userType === AdminUserType.Households && this.userId) {
            this.loadHouseholdForAcademicYear();
          }
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.academicYears = [];
          this.applyAcademicYear(null);
          this.isLoadingAcademicYears = false;
          if (this.userType === AdminUserType.Households && this.userId) {
            this.loadHouseholdForAcademicYear();
          }
          this.cdr.markForCheck();
          this.appInsightsService.log(err, SeverityLevel.error);
        },
      });
  }
  private applyAcademicYear(academicYear: AcademicYear | null): void {
    this.selectedAcademicYear = academicYear;
    this.academicYearId = academicYear?.id ?? null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (!this.preserveBreadcrumbs) {
      this.breadcrumbService.clearDynamicBreadcrumbs();
    }
  }

  createNewIssue(): void {
    this.newIssue.emit(this.userId);
  }

  editIssue(issue: IssueHistory): void {
    this.editIssueClick.emit(issue);
  }

  applyIssueTrackingSave(saved: IssueHistory): void {
    if (this.userType !== AdminUserType.Schools) return;
    this.issueTrackingData.set(this.mergeSavedIssueIntoTree(this.issueTrackingData(), saved));
    this.setSchoolStatus();
  }

  createTaskFunction(): void {
    this.router.navigate([this.parentUrl, this.userId, 'tasks', 'create'], {
      state: { providerId: this.userId, personaId: 1 },
      queryParams: { partnerTypeId: this.userTypeId }
    });
  }

  // Event handler for export data button
  onExportData(): void { }

  // Button function for sending messages
  onMessageButtonClicked(): void {
    if (this.userId) {
      const providerData = this.userData() as (ProviderData & { accountId?: string }) | null;
      const providerRecipientId = providerData?.ownerAccountId ?? providerData?.accountId ?? null;
      const schoolRecipientId = (this.userData() as SchoolRecordVm)?.hdmaAccountId ?? null;
      const householdRecipientId = (this.userData() as HouseholdRecordVm)?.householdAccountId ?? null;
      const hdmaRecipientId = providerRecipientId ?? schoolRecipientId ?? householdRecipientId ?? null;
      const isSchoolRecord = this.userType === AdminUserType.Schools;

      this.sendMessageDialogRef = this.sendMessageDialog.open(MessageCenterSendDialogComponent, {
        width: '60vw',
        maxHeight: '90vh',
        data: {
          programPartnerId: this.userId,
          programPartnerTypeId: this.userTypeId ?? null,
          recipientId: hdmaRecipientId,
          hdmaId: hdmaRecipientId,
          // Schools can message any of their contacts; providers and households only have the HDMA.
          allowRecipientSelection: isSchoolRecord,
          includeSchoolContacts: isSchoolRecord
        },
        disableClose: true,
        panelClass: 'message-center-send-dialog'
      });

      this.sendMessageDialogRef.afterClosed().subscribe({
        next: (result) => {
          // If the result is a string, then it is an error message.
          if (typeof result === 'string') {
            this.toastMessageService.showToast(ToastMessageType.Error, result);
          } else if (typeof result === 'boolean' && result) {
            this.toastMessageService.showToast(ToastMessageType.Success, 'Message sent successfully.');
          }
        },
        complete: () => {
          this.getMessagesData();
        }
      });
    } else {
      console.warn('Cannot navigate to message send: missing user id');
    }
  }

  public addDocumentsFunction(): void {
    this.router.navigate([this.parentUrl, this.userId, 'documents'], {
      state: { providerId: this.userId, personaId: 1 }
    });
  }

  onEdit(): void {
    if (!this.userId) {
      console.warn('Cannot navigate to edit: missing user id');
      return;
    }

    const stateData = { userType: this.userType };

    if (this.userType === AdminUserType.Provider) {
      stateData['providerData'] = this.userData();
    } else if (this.userType === AdminUserType.Schools) {
      stateData['schoolData'] = this.userData();
    } else if (this.userType === AdminUserType.Households) {
      stateData['householdData'] = this.userData();
    }

    this.router.navigate([this.parentUrl, this.userId, 'edit'], {
      state: stateData,
      queryParams: { partnerTypeId: this.userTypeId, userId: this.userId }
    });
  }

  viewMore(type: string): void {
    this.router.navigate([this.parentUrl, this.userId, type], {
      state: { providerId: this.userId, personaId: 1 },
      queryParams: { partnerTypeId: this.userTypeId }
    });
  }

  viewEmails(): void {
    if (!this.userId) {
      console.warn('Cannot navigate to communications center: missing user id');
      return;
    }
    const breadcrumbUserName = this.getUserName(this.userType !== AdminUserType.Households);
    const filterUserName = this.getUserName(this.userType !== AdminUserType.Schools);
    this.preserveBreadcrumbs = true;

    this.router.navigate([this.parentUrl, this.userId, 'communications-center'], {
      state: {
        breadcrumbUserName: breadcrumbUserName,
        fromUserRecord: true,
        providerId: this.userId,
        providerName: filterUserName,
        providerTypeId: this.userTypeId,
        recipientId: this.userId,
        userName: breadcrumbUserName
      },
      queryParams: { partnerTypeId: this.userTypeId }
    });
  }

  viewMessages(): void {
    if (!this.userId) {
      console.warn('Cannot navigate to messages: missing user id');
      return;
    }

    const breadcrumbUserName = this.getUserName(this.userType !== AdminUserType.Households);

    this.router
      .navigate([this.parentUrl, this.userId, 'messages'], {
        state: {
          providerId: this.userId,
          providerName: breadcrumbUserName,
          providerTypeId: this.userTypeId,
          fromProviderRecord: true
        },
        queryParams: { partnerTypeId: this.userTypeId }
      });
  }

  // Button function for adding students
  addStudentFunction(): void {
    console.log('Add student function called');
  }

  onSortChange(data: UserRecordSortableTableRow[]): void {
    this.dataSource = new MatTableDataSource<UserRecordSortableTableRow>(data);
    this.dataSource.sort = this.sortTables;
  }

  onIssueSortChange(sort: Sort): void {
    this.issueTrackingData.set(this.sort(this.issueTrackingData(), sort));
  }

  refreshInternalComments({ action, comment }: { action: string, comment?: Comment }): void {
    if (action === 'save' && comment) {
      comment.dateModified = this.systemService?.isoDateToFormatted(comment.dateModified) ?? comment.dateModified;
      const filtered = this.internalCommentsFilteredData().filter(c => c.id !== comment.id);
      this.internalCommentsFilteredData.set([comment, ...filtered].slice(0, 2));
    } else {
      this.getInternalComment();
    }
  }

  viewComments(): void {
    if (!this.commentsService) return;
    this.getInternalComment(true);
  }

  onStudentClick(details: { studentId: string; studentName: string }): void {
    const householdName = this.getUserName(false);
    const routerurl = this.router.url.startsWith('/tasks') ? `/tasks` : `/work-queue`;
    void this.router.navigate([`${routerurl}`, this.userId, 'student', details.studentId], {
      state: { providerId: this.userId, personaId: 1, providerName: householdName },
      queryParams: {
        partnerTypeId: this.userTypeId,
        accountName: this.accountName,
        academicYearId: this.selectedAcademicYearId
      }
    });
  }

  private handleDetailsChange(changes: SimpleChanges, detailsKey: string, expectedUserType: AdminUserType): void {
    if (!changes[detailsKey] || this.userType !== expectedUserType) {
      return;
    }

    const val = changes[detailsKey].currentValue;
    if (!val) {
      return;
    }

    this.userData.set(val);

    // If a Provider or School:
    const recordSvc = this.userRecordService;
    const currentUserData = this.userData();
    if (
      recordSvc &&
      currentUserData &&
      'addresses' in currentUserData &&
      ('businessName' in currentUserData || 'schoolName' in currentUserData)
    ) {
      currentUserData.address = recordSvc.parseProviderAddress(currentUserData.addresses?.[0] as Address);
    }

    if (this.userType === AdminUserType.Schools || !this.userNameFromState) {
      this.updateBreadcrumbWithUserName();
    } else {
      this.breadcrumbService.setNestedDynamicBreadcrumbs(this.parentUrl, this.userId, this.userNameFromState, {
        partnerTypeId: this.userTypeId
      });
    }

    this.userLoading = false;
  }

  private initializeComponent(): void {
    this.shouldHideGlobalSpinnerOnInitialLoad = this.shouldUseGlobalSpinner();
    if (this.shouldHideGlobalSpinnerOnInitialLoad) {
      this.loadingSpinnerService.showSpinner();
    }
    if (this.environment) {
      this.apiUrl = this.environment.adminApiUrl;
    }
    this.loading = true;
    this.userLoading = true;
  }

  private loadNavigationState(): void {
    if (history.state?.['rowData']) {
      this.rowData = history.state['rowData'];
    }

    if (history.state?.['providerName']) {
      this.userNameFromState = history.state['providerName'];
    }
  }

  private subscribeToRouteParams(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      void this.loadRecordFromRouteParams(params);
    });
  }

  private async loadRecordFromRouteParams(params: Params): Promise<void> {
    try {
      this.userId = params['id'];
      this.userTypeId = this.resolvePartnerTypeIdFromRoute();
      this.resolveAccountNameFromRoute();
      await this.loadUserData();
      // Runs after loadUserData so resolver data cannot overwrite the selected year's response.
      if (this.userType === AdminUserType.Households && !this.isLoadingAcademicYears) {
        this.loadHouseholdForAcademicYear();
      }
      this.loadAdditionalData();
    } catch (e: unknown) {
      this.logAndToastError(e);
      this.userLoading = false;
      this.completeInitialLoading();
    }
  }

  private resolvePartnerTypeIdFromRoute(): number {
    const raw = this.route.snapshot.queryParams['partnerTypeId'] as string | undefined;
    if (raw?.trim()) {
      const n = Number(raw);
      if (!Number.isNaN(n)) {
        return n;
      }
    }
    const byUserType: Partial<Record<AdminUserType, number>> = {
      [AdminUserType.Provider]: 2,
      [AdminUserType.Schools]: 1,
      [AdminUserType.Households]: 3
    };
    return byUserType[this.userType] ?? 2;
  }

  /** Household display name from URL, session cache, or loaded record */
  private resolveAccountNameFromRoute(): void {
    const raw = this.route.snapshot?.queryParams?.['accountName'];
    const fromQuery = raw == null || raw === '' ? '' : String(raw).trim();
    if (fromQuery) {
      this.accountName = fromQuery;
      this.persistHouseholdAccountName();
      return;
    }
    if (this.userType === AdminUserType.Households && this.userId) {
      this.accountName = this.readHouseholdAccountNameCache(this.userId) ?? '';
    } else {
      this.accountName = '';
    }
  }

  private householdAccountNameStorageKey(userId: string): string {
    return `k12.userRecord.householdAccountName:${userId}`;
  }

  private readHouseholdAccountNameCache(userId: string): string | null {
    try {
      const v = sessionStorage.getItem(this.householdAccountNameStorageKey(userId));
      return v?.trim() ? v : null;
    } catch {
      return null;
    }
  }

  private persistHouseholdAccountName(): void {
    if (this.userType !== AdminUserType.Households || !this.userId || !this.accountName?.trim()) {
      return;
    }
    try {
      sessionStorage.setItem(this.householdAccountNameStorageKey(this.userId), this.accountName.trim());
    } catch {
      console.error('Error persisting household account name');
    }
  }

  // Completing IV clears incomeVerificationRequired (spAssignAwardTier skips households that already
  // have a verified income), so gating on that flag alone would hide the tile at the exact moment
  // Verified Income becomes populated. A verified figure keeps the tile up on its own.
  public get showIncomeVerification(): boolean {
    const household = this.userData() as HouseholdRecordVm | null;
    return Boolean(household?.incomeVerificationRequired) || household?.verifiedIncome != null;
  }

  private deriveHouseholdAccountName(h: HouseholdRecordVm): string {
    return (
      h.householdName?.trim() ||
      h.parentName?.trim() ||
      `${h.firstName ?? ''} ${h.lastName ?? ''}`.trim()
    );
  }

  private async loadUserData(): Promise<void> {
    if (!this.userRecordService) {
      this.userLoading = false;
      return;
    }

    if (!this.userId) {
      this.userLoading = false;
      this.toastMessageService.showToast(ToastMessageType.Error, 'User ID is required');
      return;
    }

    if (this.userDetails) {
      this.userData.set(this.userDetails);
      if (this.userType === AdminUserType.Households) {
        const h = this.userDetails as HouseholdRecordVm;
        if (!this.accountName?.trim()) {
          this.accountName = this.deriveHouseholdAccountName(h);
          this.persistHouseholdAccountName();
        }
        if (this.showIncomeVerification) {
          this.loadIncomeVerificationData();
        }
      }
      this.parseAddressAndUpdateBreadcrumb();
      this.userLoading = false;
      return;
    }

    if (this.userType === AdminUserType.Provider) {
      await this.loadProviderData();
    } else {
      this.userLoading = false;
    }
  }

  private async loadProviderData(): Promise<void> {
    const recordSvc = this.userRecordService;
    if (!recordSvc) {
      this.userLoading = false;
      return;
    }
    this.userLoading = true;
    try {
      const res = await firstValueFrom(recordSvc.getProviderDetails(this.userId, this.userType));
      this.handleProviderDataSuccess(res as ProviderData);
    } catch (error: unknown) {
      this.logAndToastError(error);
      this.userLoading = false;
    }
  }

  private handleProviderDataSuccess(res: ProviderData): void {
    try {
      this.userData.set(res);

      if (!this.userData()) {
        return;
      }

      const recordSvc = this.userRecordService;
      if (!recordSvc) {
        return;
      }

      const providerData: ProviderData = {
        ...res,
        address: recordSvc.parseProviderAddress(res.addresses?.[0] as Address),
        subjects: recordSvc.parseServices(res.servicesProvided ?? []),
        type: res.providerType as 'Tutor' | 'Therapy' | 'Transportation' | undefined
      };
      this.userData.set(providerData);

      this.updateBreadcrumbForProvider();
    } catch (e: unknown) {
      this.logAndToastError(e);
    } finally {
      this.userLoading = false;
    }
  }

  private parseAddressAndUpdateBreadcrumb(): void {
    const recordSvc = this.userRecordService;
    const currentUserData = this.userData();
    // If a Provider or School:
    if (
      recordSvc &&
      currentUserData &&
      'addresses' in currentUserData &&
      ('businessName' in currentUserData || 'schoolName' in currentUserData)
    ) {
      currentUserData.address = recordSvc.parseProviderAddress(currentUserData.addresses?.[0] as Address);
    }

    if (this.userType === AdminUserType.Schools || !this.userNameFromState) {
      this.updateBreadcrumbWithUserName();
    } else {
      this.breadcrumbService.setNestedDynamicBreadcrumbs(this.parentUrl, this.userId, this.userNameFromState, {
        partnerTypeId: this.userTypeId
      });
    }
  }

  private updateBreadcrumbForProvider(): void {
    if (this.userNameFromState) {
      this.breadcrumbService.setNestedDynamicBreadcrumbs(this.parentUrl, this.userId, this.userNameFromState, {
        partnerTypeId: this.userTypeId
      });
    } else {
      this.updateBreadcrumbWithUserName();
    }
  }

  private loadAdditionalData(): void {
    if (this.userType === this.adminUserTypes.Provider) {
      this.getEmployeeData();
    }

    if (this.userType === this.adminUserTypes.Schools) {
      this.getIssueTrackingData();
    }

    this.getInternalComment();
    this.getTasksData();
    this.getMessagesData();
    this.getEmailsData();
    this.getDocumentsData();
  }

  private formatAmount(amount: number) {
    return this.currencyPipe.transform(amount, 'USD', 'symbol', '1.2-2');
  }

  private loadIncomeVerificationData(): void {
    const householdId = this.userData()?.['id'];
    this.householdService.getIncomeVerificationDetailsByHousehold(householdId).subscribe({
      next: (householdData: IncomeVerificationDetails) => {
        if (householdData) {
          const column = this.incomeVerificationColumnDefinitions.find(x => x.name === 'value');
          if(column){column.header = householdData.incomeVerificationReason}

          this.incomeVerificationData.set([
            {
              label: 'Verification Completed',
              value: householdData.verificationComplete ? 'Y' : 'N'
            },
            {
              label: 'Income Declared',
              value: householdData.incomeDeclared ? this.formatAmount(householdData.incomeDeclared) : '-'
            },
            {
              label: 'Verified Income',
              // Null-checked rather than truthy-checked so a verified income of $0 is not shown as
              // the same '-' that means "not verified yet".
              value: householdData.verifiedIncome != null ? this.formatAmount(householdData.verifiedIncome) : '-'
            },
            {
              label: 'Verified HH Size',
              value: householdData.verifiedHouseholdSize || '-'
            },
            {
              label: 'New Tier if Applicable',
              value: householdData.newTier || '-'
            }
          ]);
        }
      },
      error: (error: unknown) => {
        this.logAndToastError(error);
      }
    });
  }

  private getTasksData(): void {
    const taskFilter = {
      programPartnerIds: [this.userId]
    };

    this.tasksService.getFilteredTasks(taskFilter).subscribe({
      next: (res) => {
        this.taskData.set(res.body ?? []);

        this.taskData().sort((a, b) => {
          const dateA = a.dateCreated ? new Date(a.dateCreated).getTime() : 0;
          const dateB = b.dateCreated ? new Date(b.dateCreated).getTime() : 0;
          return dateB - dateA;
        });

        this.taskData.set(this.taskData().slice(0, 4));
      },
      error: (error: unknown) => {
        this.logAndToastError(error);
      }
    });
  }

  private getEmployeeData(): void {
    if (!this.employeeService) return;

    const payload: EmployeeSummaryAdminPayload = {
      Filter: {
        PageNumber: 1,
        PageSize: 4
      }
    };
    this.employeeService.GetAllEmployeesSummaryAdmin(this.userId, payload).subscribe({
      next: (res: EmployeeSummary[]) => {
        const rows = Array.isArray(res) ? res : [];
        const sorted = [...rows].sort((a, b) => {
          const aLast = (a?.lastName ?? '').trim().toLowerCase();
          const bLast = (b?.lastName ?? '').trim().toLowerCase();
          return aLast.localeCompare(bLast);
        });

        this.employeesData.set(sorted.slice(0, 4));

        this.employeesData().forEach((employee) => {
          if (employee?.subServices?.length && employee.subServices.length > 0) {
            const subServiceName = employee.subServices[0].name;
            let subServiceCount = '';
            if (employee.subServices.length > 1) {
              subServiceCount = ' + ' + (employee.subServices.length - 1);
            }
            employee.subServices = [{ name: subServiceName + subServiceCount } as EmployeeSubService];
          }
        });
      },
      error: (error: unknown) => {
        this.logAndToastError(error);
      }
    });
  }

  private getDocumentsData(): void {
    const containerType = PartnerTypeIdToUserType[this.userTypeId];
    this.documentsService.getDocumentList(containerType, this.userId).subscribe({
      next: (res: DocumentsList[]) => {
        if (!Array.isArray(res)) {
          this.documentData.set([]);
          this.completeInitialLoading();
          return;
        }
        this.documentData.set(res);
        this.documentData().forEach((doc) => {
          doc.name = doc.metadata?.fileName ?? '';
          doc.type = doc.metadata?.Type ?? '';
          doc.version = doc.version == null ? '' : String(doc.version);
          doc.documentType = doc.metadata?.documentType ?? '';
        });

        // Sort the documentData array by most recently modified
        this.documentData.set([...this.documentData()].sort((a, b) => {
          const dateA = a.lastModified ? new Date(a.lastModified).getTime() : 0;
          const dateB = b.lastModified ? new Date(b.lastModified).getTime() : 0;
          return dateB - dateA;
        }));
        this.documentData.set(this.documentData().slice(0, 4));
        this.completeInitialLoading();
      },
      error: (error: unknown) => {
        this.logAndToastError(error);
        this.completeInitialLoading();
      }
    });
  }

  get showSchoolLoadingSpinner(): boolean {
    return this.userType === this.adminUserTypes.Schools && this.loading;
  }

  public getMessagesData(): void {
    if (!this.messagesService) return;

    this.messagesService.getAccountMessageTopics(this.userId).subscribe({
      next: (res: AccountMessageTopic[]) => {
        this.messagesData.set(res.map((item) => ({
          topicId: item.id,
          messageSubject: item.parentCategoryName,
          childSubject: item.childCategoryName ?? '',
          lastMessageDate: item.dateCreated
        })));
        this.messagesData.set([...this.messagesData()].sort((a, b) => {
          const dateA = a.lastMessageDate ? new Date(a.lastMessageDate).getTime() : 0;
          const dateB = b.lastMessageDate ? new Date(b.lastMessageDate).getTime() : 0;
          return dateB - dateA;
        }));
        this.messagesData.set(this.messagesData().slice(0, 4));
      },
      error: (error: unknown) => {
        this.logAndToastError(error);
      }
    });
  }

  private getEmailsData(): void {
    if (!this.communicationsService) return;

    const filterUserName = this.getUserName(this.userType !== AdminUserType.Schools);
    const payload: CommunicationTableMetadata = {
      communicationTemplateId: null,
      communicationTypeId: 1,
      endDate: null,
      orderBy: 'StartDate',
      orderDirection: 'desc',
      pageNumber: 1,
      pageSize: 4,
      programPartnerTypeId: this.userTypeId,
      searchTerm: filterUserName,
      startDate: null
    };

    this.communicationsService.getDashboardCommunicationGrid(payload).subscribe({
      next: (res: Paged<CommunicationTableItem>) => {
        this.emailData.set((res.items ?? []).map((item) => ({
          subject: item.template ?? '',
          startDate: item.startDate ? new Date(item.startDate).toISOString() : ''
        })));
      },
      error: (error: unknown) => {
        this.logAndToastError(error);
      }
    });
  }

  private getIssueTrackingData(): void {
    if (!this.issueTrackingService || this.userType !== AdminUserType.Schools) return;
    this.issueTrackingData.set([]);
    this.schoolRestrictionMessage.set(null);
    this.issueTrackingService
      .getIssueRecordsPageForUserRecord({
        programPartnerId: this.userId
      })
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (items: IssueHistory[]) => {
          // Default initial ordering: Date Reported (dateReported) desc.
          this.issueTrackingData.set(this.sort(items ?? [], { active: 'dateReported', direction: 'desc' }));
          this.setSchoolStatus();
        },
        error: (error: unknown) => {
          this.issueTrackingData.set([]);
          this.setSchoolStatus();
          this.logAndToastError(error);
        }
      });
  }

  private loadHouseholdForAcademicYear(force = false): void {
    const requestKey = `${this.userId}:${this.academicYearId ?? ''}`;
    if (!force && requestKey === this.lastHouseholdRequestKey) {
      return;
    }
    this.lastHouseholdRequestKey = requestKey;
    this.studentsData.set([]);
    this.studentsLoading = true;
    this.userLoading = true;
    this.householdYearChange$.next(this.academicYearId);
  }

  private applyHouseholdYearDetails(details: HouseHoldDetails): void {
    const students = details.students ?? [];
    this.userData.set({
      id: details.id,
      firstName: details.firstName,
      lastName: details.lastName,
      middleName: details.middleName,
      email: details.email ?? '',
      phoneNumber: details.phoneNumber,
      dob: details.dob ?? '',
      ssn: details.ssn,
      driversLicense: details.driversLicense,
      issuingState: details.issuingState,
      householdIncome: details.householdIncome,
      disclosedIncome: details.disclosedIncome,
      verifiedIncome: details.verifiedIncome,
      verifiedIncomeUpdatedDate: details.verifiedIncomeUpdatedDate,
      disclosedInformation: details.disclosedInformation,
      householdAccountId: details.householdAccountId,
      householdName: details.houseHoldName,
      householdMemberCount: details.householdMemberCount,
      incomeVerificationRequired: details.incomeVerificationRequired,
      incomeVerificationReason: details.incomeVerificationReason,
      ncResidency: details.ncResidency,
      ncResidencyVerified: details.ncResidencyVerified,
      parentName: [details.firstName, details.middleName, details.lastName].filter(Boolean).join(' '),
      pidNumber: details.pidNumber ?? '',
      householdTier: details.householdTier,
      address1: details.address1,
      address2: details.address2,
      city: details.city,
      state: details.state,
      zip: details.zip,
      county: details.county,
      students: students.map((student) => ({
        id: student.id,
        firstName: student.firstName,
        lastName: student.lastName,
        grade: student.grade ?? ''
      }))
    });
    this.userLoading = false;
    this.applyHouseholdStudents(students);
  }

  private applyHouseholdStudents(students: HouseholdStudentRecord[]): void {
    this.studentsData.set(students.map((student) => ({
      ...student,
      studentName: `${student.firstName} ${student.lastName}`.trim()
    })));
    this.studentsLoading = false;
  }

  // Method to update breadcrumb with user name
  private updateBreadcrumbWithUserName(): void {
    const currentUserData = this.userData();
    if (!currentUserData) {
      return;
    }

    if (this.userType === AdminUserType.Schools) {
      const school = currentUserData as SchoolRecordVm;
      const schoolLabel = formatSchoolAccountLabel(
        school.schoolName,
        school.institutionName || school.schoolName
      );
      this.breadcrumbService.setNestedDynamicBreadcrumbs(this.parentUrl, this.userId, schoolLabel, {
        partnerTypeId: this.userTypeId
      });
      return;
    }

    if ('businessName' in currentUserData && currentUserData.businessName) {
      this.breadcrumbService.setNestedDynamicBreadcrumbs(this.parentUrl, this.userId, currentUserData.businessName, {
        partnerTypeId: this.userTypeId
      });
    } else if ('pidNumber' in currentUserData && currentUserData.pidNumber) {
      console.log('Using PID number for breadcrumb:', currentUserData.pidNumber);
      this.breadcrumbService.setNestedDynamicBreadcrumbs(
        this.parentUrl,
        this.userId,
        `${[
          currentUserData.firstName, currentUserData.middleName, currentUserData.lastName
        ].filter(Boolean).join(' ') || '--'
        }`,
        { partnerTypeId: this.userTypeId }
      );
    } else if (
      'firstName' in currentUserData && currentUserData['firstName'] &&
      'lastName' in currentUserData && currentUserData['lastName']
    ) {
      const userName = `${currentUserData.firstName} ${currentUserData.lastName}`;
      this.breadcrumbService.setNestedDynamicBreadcrumbs(this.parentUrl, this.userId, userName, {
        partnerTypeId: this.userTypeId
      });
    }
  }

  private getUserName(breadcrumb: boolean = true): string {
    switch (this.userType) {
      case AdminUserType.Provider:
        return (this.userData() as ProviderData)?.businessName ?? '';
      case AdminUserType.Schools: {
        const school = this.userData() as SchoolRecordVm;
        if (!breadcrumb) {
          return school?.schoolName?.trim() ?? '';
        }
        return formatSchoolAccountLabel(school?.schoolName, school?.institutionName || school?.schoolName);
      }
      case AdminUserType.Households: {
        const h = this.userData() as HouseholdRecordVm | null;
        if (!breadcrumb) {
          return [h?.firstName, h?.middleName, h?.lastName].filter(Boolean).join(' ').trim();
        }
        return (
          this.accountName?.trim() ||
          (h ? this.deriveHouseholdAccountName(h) : '')
        );
      }
      default:
        return this.userNameFromState || 'User';
    }
  }

  private getInternalComment(isOpen?: boolean): void {
    if (!this.commentsService) return;
    this.commentsService.getComments(this.userId, this.commentType.SEAAUseOnly).subscribe({
      next: (res: Comment[]) => {
        this.internalCommentsData.set(res);
        this.internalCommentsData().forEach((el) => {
          el.dateModified = this.systemService?.isoDateToFormatted(el.dateModified) ?? el.dateModified;
        });
        this.internalCommentsFilteredData.set(this.internalCommentsData().slice(0, 2));
        if (isOpen) {
          this.openCommentsDrawer.emit(this.internalCommentsData());
        }
      },
      error: (error: unknown) => {
        this.logAndToastError(error);
      }
    });
  }

  private sort<T extends object>(data: T[], event: Sort): T[] {
    if (!event.direction) return data;

    const isAsc = event.direction === 'asc';

    return [...data].sort((a: T, b: T) => {

      // Handle Issue Tracking OnHoldStatus(Restrictions)
      if (event.active === 'onHoldStatus') {
        return this.sortOnHoldStatus(a as unknown as IssueHistory, b as unknown as IssueHistory, isAsc);
      } else {
        // For other columns, sort based on the value of the active column
        const aValue = (a as Record<string, unknown>)[event.active];
        const bValue = (b as Record<string, unknown>)[event.active];

        if (aValue == null) return 1;
        if (bValue == null) return -1;

        // Handle strings
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return this.sortString(aValue, bValue, isAsc);
        }

        // Handle numbers
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return isAsc
            ? aValue - bValue
            : bValue - aValue;
        }

        return 0;
      }
    });
  }

  private sortOnHoldStatus(a: IssueHistory, b: IssueHistory, isAsc: boolean): number {
    const aOnHold = this.getRedCount(a);
    const bOnHold = this.getRedCount(b);
    let comparison = 0;
    if (aOnHold < bOnHold) {
      comparison = -1;
    } else if (aOnHold > bOnHold) {
      comparison = 1;
    }
    return isAsc ? comparison : -comparison;
  }

  private sortString(a: string, b: string, isAsc: boolean): number {
    const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;
    if (isoRegex.test(a) && isoRegex.test(b)) {
      const aDate = new Date(a);
      const bDate = new Date(b);

      const aIsValidDate = aDate instanceof Date && !Number.isNaN(aDate.getTime());
      const bIsValidDate = bDate instanceof Date && !Number.isNaN(bDate.getTime());

      // Handle date strings FIRST
      if (aIsValidDate && bIsValidDate) {
        return isAsc
          ? aDate.getTime() - bDate.getTime()
          : bDate.getTime() - aDate.getTime();
      }
      // If not valid dates, fall through to string comparison below
    }
    // Regular string comparison (also fallback if not valid dates)
    return isAsc ? a.localeCompare(b) : b.localeCompare(a);
  }

  private getRedCount(issue: IssueHistory): number {
    let count = 0;
    if (issue.certificationRestricted) count++;
    if (issue.endorsementRestricted) count++;
    if (issue.paymentRestricted) count++;
    return count;
  }

  /** Merges an issue row returned from save (create/update) into the user-record grid tree. */
  private mergeSavedIssueIntoTree(items: IssueHistory[], saved: IssueHistory): IssueHistory[] {
    const i = items.findIndex((row) => row.id === saved.id);
    if (i >= 0) {
      return items.map((row, j) => (j === i ? this.withCreatedByNameFilled(saved) : row));
    }

    const parentId = saved.parentIssueRecordId?.trim();
    const currentMatch = parentId ? this.findParentInList(items, parentId) : null;

    if (!currentMatch) {
      return [this.withCreatedByNameFilled(saved), ...items];
    }

    const { root, parent, historyList } = currentMatch;
    const outputHistory =
      saved.history?.length ? saved : { ...saved, history: [{ ...parent, history: null }, ...historyList] };

    return [this.withCreatedByNameFilled(outputHistory), ...items.filter((row) => row.id !== root.id)];
  }

  private findParentInList(items: IssueHistory[], parentId: string): IssueThreadParentMatch | null {
    for (const root of items) {
      if (root.id === parentId) {
        return { root, parent: root, historyList: root.history ?? [] };
      }
      const issueHistory = root.history;
      if (!issueHistory?.length) continue;
      const idx = issueHistory.findIndex((row) => row.id === parentId);
      if (idx >= 0) {
        return { root, parent: issueHistory[idx], historyList: issueHistory.slice(idx + 1) };
      }
    }
    return null;
  }

  private withCreatedByNameFromParent(row: IssueHistory, parent?: IssueHistory | null): IssueHistory {
    const name = parent?.createdByName?.trim();
    if (row.createdByName?.trim() || !name) return row;
    return { ...row, createdByName: name };
  }

  private withCreatedByNameFilled(head: IssueHistory): IssueHistory {
    const history = head.history;
    if (!history?.length) return head;

    const historyItems = [...history];
    for (let j = historyItems.length - 1; j >= 0; j--) {
      historyItems[j] = this.withCreatedByNameFromParent(historyItems[j], historyItems[j + 1]);
    }
    return this.withCreatedByNameFromParent({ ...head, history: historyItems }, historyItems[0]);
  }

  private logAndToastError(error: unknown): void {
    const message = this.describeError(error);
    let logged: Error;
    if (error instanceof HttpErrorResponse) {
      // Log the request that failed alongside the message, so the entry can be matched to server logs.
      logged = new Error(`HTTP ${error.status} ${error.url ?? ''}: ${message}`);
    } else {
      logged = error instanceof Error ? error : new Error(message);
    }
    this.appInsightsService.log(logged, SeverityLevel.error);
    this.toastMessageService.showToast(ToastMessageType.Error, message);
  }

  /**
   * Turns any thrown value into text a person can act on. An HttpErrorResponse is not an Error, so
   * String() on it produced "[object Object]" for every failed request on this page. Prefer what the
   * server said, adding its trace id so the failure can be found in the logs.
   */
  private describeError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const serverMessage = this.readServerMessage(error.error);
      if (serverMessage) {
        const traceId = (error.error as { traceId?: unknown } | null)?.traceId;
        return typeof traceId === 'string' && traceId ? `${serverMessage} Reference: ${traceId}` : serverMessage;
      }
      if (error.status === 0) {
        return 'Unable to reach the server. Please try again.';
      }
      // Angular's own message only restates the URL and status. A calling service may have replaced it
      // with a better explanation before rethrowing.
      if (error.message && !error.message.startsWith('Http failure')) {
        return error.message;
      }
      return `Something went wrong (HTTP ${error.status}). Please try again.`;
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return typeof error === 'string' && error.trim() ? error : 'Something went wrong. Please try again.';
  }

  /** Reads a validation-result array, a plain string, or a ProblemDetails-style body. */
  private readServerMessage(body: unknown): string | null {
    if (typeof body === 'string') {
      return body.trim() || null;
    }

    if (Array.isArray(body)) {
      const messages = body
        .map((item) => (item as { errorMessage?: unknown } | null)?.errorMessage)
        .filter((text): text is string => typeof text === 'string' && text.trim() !== '');
      return messages.length > 0 ? messages.join(' ') : null;
    }

    if (body && typeof body === 'object') {
      const { detail, Error: errorText, message } = body as { detail?: unknown; Error?: unknown; message?: unknown };
      return [detail, errorText, message].find((text): text is string => typeof text === 'string' && text.trim() !== '') ?? null;
    }

    return null;
  }

  private shouldUseGlobalSpinner(): boolean {
    const raw = this.route.snapshot.queryParams['partnerTypeId'] as string | undefined;
    if (raw?.trim()) {
      const parsed = Number(raw);
      if (!Number.isNaN(parsed)) {
        return PartnerTypeIdToUserType[parsed] !== AdminUserType.Schools;
      }
    }
    return this.userType !== AdminUserType.Schools;
  }

  private completeInitialLoading(): void {
    this.loading = false;
    if (this.shouldHideGlobalSpinnerOnInitialLoad) {
      this.loadingSpinnerService.hideSpinner();
      this.shouldHideGlobalSpinnerOnInitialLoad = false;
    }
  }

  setSchoolStatus(): void {
    const hasCertificationRestriction = this.issueTrackingData().some((issue) => issue.certificationRestricted);
    const hasEndorsementRestriction = this.issueTrackingData().some((issue) => issue.endorsementRestricted);
    const hasPaymentRestriction = this.issueTrackingData().some((issue) => issue.paymentRestricted);

    const restrictions: string[] = [];
    if (hasCertificationRestriction) restrictions.push('Certification');
    if (hasEndorsementRestriction) restrictions.push('Endorsement');
    if (hasPaymentRestriction) restrictions.push('Payment');

    if (restrictions.length > 0) {
      this.schoolStatus.set(ProviderInfoStatus.OnHold);
      this.schoolRestrictionMessage.set(`Restrictions: ${restrictions.join(', ')}`);
      return;
    }

    this.schoolStatus.set(ProviderInfoStatus.Approved);
    this.schoolRestrictionMessage.set('No Restrictions');
  }

  get canEdit(): boolean {
    if(this.userType !== AdminUserType.Households) {
      return true;
    }
    const startDateValue = this.selectedAcademicYear?.startDate;
    const endDateValue = this.selectedAcademicYear?.endDate;

    if (!startDateValue || !endDateValue) {
      return false;
    }

    const startDate = new Date(startDateValue);
    const endDate = new Date(endDateValue);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      return false;
    }

    return FormatDatePipe.isTodayWithin(startDate, endDate);
  }
}
